import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { cart, updateQuantity, removeFromCart } = useCart();
  const [cartSummary, setCartSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  const calculateCart = async () => {
    setLoading(true);
    try {
      const items = cart.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        size: item.size,
        color: item.color
      }));

      const response = await axios.post(`${API}/cart/calculate`, { items });
      setCartSummary(response.data);
    } catch (error) {
      console.error('Error calculating cart:', error);
      toast.error('Failed to calculate cart total');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cart.length > 0) {
      calculateCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setProcessing(true);
    try {
      const items = cart.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        size: item.size,
        color: item.color
      }));

      const originUrl = window.location.origin;

      const response = await axios.post(`${API}/checkout/session`, {
        items,
        originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to create checkout session');
      setProcessing(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[hsl(var(--cream))] grain-texture">
        <Navbar />
        <div data-testid="empty-cart" className="container mx-auto px-6 lg:px-12 pt-32 pb-16 text-center">
          <h1 className="font-heading text-4xl text-[hsl(var(--charcoal))] mb-4">Your Cart is Empty</h1>
          <p className="font-body text-lg text-[hsl(var(--stone-gray))] mb-8">
            Add some eco-friendly bags to get started!
          </p>
          <button
            onClick={() => navigate('/products')}
            className="btn-primary bg-[hsl(var(--earth-green))] text-[hsl(var(--cream))] px-8 py-4 rounded-2xl font-accent font-semibold uppercase tracking-wider shadow-lg"
          >
            Shop Now
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--cream))] grain-texture">
      <Navbar />

      <section className="pt-32 pb-16">
        <div className="container mx-auto px-6 lg:px-12">
          <h1 className="font-heading text-4xl lg:text-5xl text-[hsl(var(--charcoal))] mb-12">Shopping Cart</h1>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, index) => (
                <div
                  key={`${item.productId}-${item.size}-${item.color}`}
                  data-testid={`cart-item-${index}`}
                  className="bg-white rounded-2xl p-6 shadow-md flex flex-col sm:flex-row gap-6"
                >
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    className="w-full sm:w-32 h-32 object-cover rounded-xl"
                  />

                  <div className="flex-1">
                    <h3 className="font-heading text-xl text-[hsl(var(--charcoal))] mb-2">{item.product.name}</h3>
                    <p className="font-body text-sm text-[hsl(var(--stone-gray))] mb-2">
                      Size: {item.size} | Color: {item.color}
                    </p>
                    <p className="font-heading text-lg text-[hsl(var(--earth-green))]">${item.product.price}</p>
                  </div>

                  <div className="flex sm:flex-col items-center justify-between sm:justify-center gap-4">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`decrease-quantity-${index}`}
                        onClick={() => updateQuantity(item.productId, item.size, item.color, item.quantity - 1)}
                        className="w-8 h-8 rounded-full bg-[hsl(var(--sand))] flex items-center justify-center hover:bg-[hsl(var(--earth-green))] hover:text-white transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span data-testid={`item-quantity-${index}`} className="font-accent font-semibold text-lg w-8 text-center">{item.quantity}</span>
                      <button
                        data-testid={`increase-quantity-${index}`}
                        onClick={() => updateQuantity(item.productId, item.size, item.color, item.quantity + 1)}
                        className="w-8 h-8 rounded-full bg-[hsl(var(--sand))] flex items-center justify-center hover:bg-[hsl(var(--earth-green))] hover:text-white transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    <button
                      data-testid={`remove-item-${index}`}
                      onClick={() => removeFromCart(item.productId, item.size, item.color)}
                      className="text-[hsl(var(--sunrise-coral))] hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-8 shadow-lg sticky top-24">
                <h2 className="font-heading text-2xl text-[hsl(var(--charcoal))] mb-6">Order Summary</h2>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 text-[hsl(var(--earth-green))] animate-spin" />
                  </div>
                ) : cartSummary ? (
                  <div className="space-y-4">
                    <div className="flex justify-between font-body text-[hsl(var(--stone-gray))]">
                      <span>Subtotal</span>
                      <span data-testid="subtotal">${cartSummary.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-body text-[hsl(var(--stone-gray))]">
                      <span>Tax</span>
                      <span data-testid="tax">${cartSummary.tax.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-[hsl(var(--sand))] pt-4">
                      <div className="flex justify-between font-heading text-xl text-[hsl(var(--charcoal))]">
                        <span>Total</span>
                        <span data-testid="total">${cartSummary.total.toFixed(2)}</span>
                      </div>
                    </div>

                    <button
                      data-testid="checkout-button"
                      onClick={handleCheckout}
                      disabled={processing}
                      className="w-full btn-primary bg-[hsl(var(--earth-green))] text-[hsl(var(--cream))] px-8 py-4 rounded-2xl font-accent font-semibold uppercase tracking-wider shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {processing ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Proceed to Checkout'
                      )}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckoutPage;